import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { 
  getUserTeams, 
  getInvoicesForReport, 
  formatCurrency,
  formatDate 
} from '../services/firebaseService';
import { 
  Team, 
  Invoice, 
  DateRange 
} from '../types';
import './ByTeamReport.css';

interface TeamData {
  teamName: string;
  invoices: Invoice[];
  totalValue: number;
  totalInvoices: number;
  avgValue: number;
}

const ByTeamReport: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'team' | 'invoices' | 'value'>('value');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: '',
    endDate: ''
  });

  const loadUserTeams = async () => {
    try {
      const userTeams = await getUserTeams(user!.uid);
      setTeams(userTeams);
      setSelectedTeams([]);
    } catch (error) {
      console.error('Error loading teams:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserTeams();
      setInitialDateRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setInitialDateRange = () => {
    const currentDay = new Date().getDate();
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1);

    let startDate, endDate;
    
    if (currentDay < 26) {
      startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-26`;
      endDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-25`;
    } else {
      startDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-26`;
      endDate = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}-25`;
    }

    setDateRange({ startDate, endDate });
  };

  const loadReportData = async () => {
    if (selectedTeams.length === 0 || !dateRange.startDate || !dateRange.endDate) {
      alert('Selecione pelo menos uma equipe e defina o período.');
      return;
    }

    setLoading(true);
    try {
      const allInvoices: Invoice[] = [];
      
      for (const teamId of selectedTeams) {
        const teamInvoices = await getInvoicesForReport(teamId, dateRange.startDate, dateRange.endDate);
        allInvoices.push(...teamInvoices);
      }

      setInvoices(allInvoices);
    } catch (error) {
      console.error('Error loading report data:', error);
      alert('Erro ao carregar dados do relatório.');
    } finally {
      setLoading(false);
    }
  };


  const teamData = useMemo(() => {
    const teamMap = new Map<string, TeamData>();

    invoices.forEach(invoice => {
      const teamName = invoice.team || 'Sem Equipe';
      const value = invoice.totalWithFactor || 0;

      if (teamMap.has(teamName)) {
        const existing = teamMap.get(teamName)!;
        existing.invoices.push(invoice);
        existing.totalValue += value;
        existing.totalInvoices += 1;
        existing.avgValue = existing.totalValue / existing.totalInvoices;
      } else {
        teamMap.set(teamName, {
          teamName,
          invoices: [invoice],
          totalValue: value,
          totalInvoices: 1,
          avgValue: value
        });
      }
    });

    return Array.from(teamMap.values());
  }, [invoices]);

  const filteredAndSortedData = useMemo(() => {
    let filtered = teamData.filter(team =>
      searchTerm === '' ||
      team.teamName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'team':
          aValue = a.teamName;
          bValue = b.teamName;
          break;
        case 'invoices':
          aValue = a.totalInvoices;
          bValue = b.totalInvoices;
          break;
        case 'value':
          aValue = a.totalValue;
          bValue = b.totalValue;
          break;
        default:
          aValue = a.totalValue;
          bValue = b.totalValue;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sortOrder === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
      }
    });
  }, [teamData, searchTerm, sortBy, sortOrder]);

  const reportSummary = useMemo(() => {
    const totalTeams = filteredAndSortedData.length;
    const totalInvoices = filteredAndSortedData.reduce((sum, team) => sum + team.totalInvoices, 0);
    const totalValue = filteredAndSortedData.reduce((sum, team) => sum + team.totalValue, 0);
    const avgValuePerTeam = totalTeams > 0 ? totalValue / totalTeams : 0;
    const avgInvoicesPerTeam = totalTeams > 0 ? totalInvoices / totalTeams : 0;

    return {
      totalTeams,
      totalInvoices,
      totalValue,
      avgValuePerTeam,
      avgInvoicesPerTeam
    };
  }, [filteredAndSortedData]);

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams(prev => 
      prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const handleSelectAllTeams = () => {
    setSelectedTeams(teams.map(team => team.id));
  };

  const handleDeselectAllTeams = () => {
    setSelectedTeams([]);
  };

  const handleSort = (column: 'team' | 'invoices' | 'value') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const toggleTeamExpansion = (teamName: string) => {
    setExpandedTeams(prev => {
      const newSet = new Set(prev);
      if (newSet.has(teamName)) {
        newSet.delete(teamName);
      } else {
        newSet.add(teamName);
      }
      return newSet;
    });
  };

  const exportToCSV = () => {
    const headers = [
      'Equipe', 'Data', 'ID (Planilha)', 'Projeto', 'Título', 'PMS', 'OS', 'Município', 'Valor'
    ].join(';');

    const filteredInvoices = invoices.filter(invoice => 
      searchTerm === '' ||
      (invoice.team && invoice.team.toLowerCase().includes(searchTerm.toLowerCase())) ||
      invoice.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.project.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.city.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const csvData = filteredInvoices.map(invoice => [
      `"${invoice.team || 'Sem Equipe'}"`,
      `"${formatDate(invoice.date)}"`,
      `"${invoice.id}"`,
      `"${invoice.project}"`,
      `"${invoice.title}"`,
      `"${invoice.pms}"`,
      `"${invoice.os}"`,
      `"${invoice.city}"`,
      (invoice.totalWithFactor || 0).toFixed(2)
    ].join(';')).join('\n');

    const csvContent = [headers, csvData].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio-por-equipe-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <i className="fas fa-sort"></i>;
    return sortOrder === 'asc' ? <i className="fas fa-sort-up"></i> : <i className="fas fa-sort-down"></i>;
  };

  return (
    <div className="byteam-report-container">
      <div className="report-header">
        <div className="header-content">
          <h1>Relatório Por Equipe</h1>
          <button onClick={() => window.close()} className="close-btn">
            <i className="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>

      <div className="report-content">
        {/* Filters Section */}
        <div className="filters-section">
          <div className="filter-group">
            <div className="teams-header">
              <h3>Equipes</h3>
              <div className="team-controls">
                <button onClick={handleSelectAllTeams} className="control-btn">
                  Selecionar Todas
                </button>
                <button onClick={handleDeselectAllTeams} className="control-btn">
                  Desmarcar Todas
                </button>
              </div>
            </div>
            <div className="teams-grid">
              {teams.map((team) => (
                <label key={team.id} className="team-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTeams.includes(team.id)}
                    onChange={() => handleTeamToggle(team.id)}
                  />
                  {team.name}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3>Período</h3>
            <div className="date-inputs">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="date-input"
              />
              <span>até</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="date-input"
              />
            </div>
          </div>

          <div className="filter-group">
            <h3>Filtros</h3>
            <div className="filter-controls">
              <input
                type="text"
                placeholder="Pesquisar equipe..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="action-buttons">
            <button
              onClick={loadReportData}
              disabled={loading || selectedTeams.length === 0}
              className="load-btn"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Carregando...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Gerar Relatório
                </>
              )}
            </button>

            {invoices.length > 0 && (
              <button onClick={exportToCSV} className="export-btn">
                <i className="fas fa-download"></i>
                Exportar CSV
              </button>
            )}
          </div>
        </div>

        {/* Summary Section */}
        {teamData.length > 0 && (
          <div className="summary-section">
            <h3>Resumo</h3>
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-value">{reportSummary.totalTeams}</div>
                <div className="card-label">Total de Equipes</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.totalInvoices}</div>
                <div className="card-label">Total de Boletins</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{formatCurrency(reportSummary.totalValue)}</div>
                <div className="card-label">Valor Total</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{formatCurrency(reportSummary.avgValuePerTeam)}</div>
                <div className="card-label">Valor Médio por Equipe</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{reportSummary.avgInvoicesPerTeam.toFixed(1)}</div>
                <div className="card-label">Boletins Médio por Equipe</div>
              </div>
            </div>
          </div>
        )}

        {/* Results Table */}
        {invoices.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>Resultados ({invoices.length} boletins)</h3>
            </div>

            <div className="table-wrapper">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Equipe</th>
                    <th>Data</th>
                    <th>ID (Planilha)</th>
                    <th>Projeto</th>
                    <th>Título</th>
                    <th>PMS</th>
                    <th>OS</th>
                    <th>Município</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices
                    .filter(invoice => 
                      searchTerm === '' ||
                      (invoice.team && invoice.team.toLowerCase().includes(searchTerm.toLowerCase())) ||
                      invoice.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      invoice.project.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      invoice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      invoice.city.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((invoice, index) => (
                    <tr key={invoice.key} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                      <td className="team-cell">{invoice.team || 'Sem Equipe'}</td>
                      <td className="date-cell">{formatDate(invoice.date)}</td>
                      <td className="id-cell">{invoice.id}</td>
                      <td className="project-cell" title={invoice.project}>{invoice.project}</td>
                      <td className="title-cell" title={invoice.title}>{invoice.title}</td>
                      <td className="pms-cell" title={invoice.pms}>{invoice.pms}</td>
                      <td className="os-cell" title={invoice.os}>{invoice.os}</td>
                      <td className="city-cell" title={invoice.city}>{invoice.city}</td>
                      <td className="value-cell">{formatCurrency(invoice.totalWithFactor || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {invoices.filter(invoice => 
              searchTerm === '' ||
              (invoice.team && invoice.team.toLowerCase().includes(searchTerm.toLowerCase())) ||
              invoice.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
              invoice.project.toLowerCase().includes(searchTerm.toLowerCase()) ||
              invoice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
              invoice.city.toLowerCase().includes(searchTerm.toLowerCase())
            ).length === 0 && invoices.length > 0 && (
              <div className="empty-results">
                <i className="fas fa-search"></i>
                <p>Nenhum boletim encontrado com os filtros aplicados.</p>
              </div>
            )}
          </div>
        )}

        {invoices.length === 0 && !loading && (
          <div className="no-data">
            <i className="fas fa-users"></i>
            <h3>Nenhum dado carregado</h3>
            <p>Configure os filtros e clique em "Gerar Relatório" para visualizar os dados por equipe.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ByTeamReport;
